import { computed, makeObservable, observable } from "mobx";
import { MrbBaseEditViewStore } from "mrb/core";
import { PodcastEpisodeEditForm } from "administration/podcast/forms";
import { MrbFileUploadViewStore } from "mrb/components/media";
import { head, isNil } from "lodash";
import { NakiNigunAlbumCoverImageUploadViewStore } from "administration/naki-nigun/stores";
import { httpStatusCodes } from "mrb/core/constants";
import { localizationService } from "common/localization";

class NakiNigunAlbumTrackEditViewStore extends MrbBaseEditViewStore {
    @observable.ref navigateBack = null;

    @computed get file() {
        return head(this.fileUploadStore.files);
    }

    @computed get showScheduledRelease() {
        return this.form.$("isActive").value === false;
    }

    constructor(rootStore, { routeStore }) {
        const id = rootStore.routerStore.routerState.params.id;
        super(rootStore, {
            id: id,
            name: "NakiNigunAlbumTrackEdit",
            FormClass: PodcastEpisodeEditForm,
            autoFocusField: "title",
            successCreateNotification: null,
            successUpdateNotification: null,
            redirectOnUpdateSuccess: () =>
                this.rootStore.routerStore.goTo("master.administration.naki-nigun.artist.album.tracks", { id: id }),
        });
        this.routeStore = routeStore;
        this.albumId = id;
        makeObservable(this);

        this.coverImageUploadViewStore = new NakiNigunAlbumCoverImageUploadViewStore(this.rootStore, {
            routeStore: this.routeStore,
            width: 200,
            height: 200,
        });

        this.fileUploadStore = new MrbFileUploadViewStore(this.rootStore, {
            acceptedFiles: "audio/mpeg,.aac",
            maxSize: 500000000, // 500 MB in bytes
            showValidation: false,
            errorsMap: {
                "file-invalid-type": () => {
                    return "File type must be .mp3 or .aac";
                },
                "file-too-large": () => {
                    return "File is larger than 500MB";
                },
            },
        });

        this.reaction(
            () => this.file,
            (file) => {
                if (!isNil(file)) {
                    const fileName = file.name ? file.name.split(".")[0] : null;
                    if (fileName) {
                        this.form.$("title").value = fileName;
                    }
                }
            }
        );
    }

    async get(id) {
        const album = await this.routeStore.getNakiNigunAlbum(id);
        this.navigateBack = this._createNavigateBackAction(album);
        return { isActive: true };
    }

    async update(data) {
        if (!this.file || !data || !data.title) {
            return;
        }

        let formData = new FormData();
        formData.append("file", this.file);
        formData.append("title", data.title);
        formData.append("isActive", data.isActive ? "true" : "false");
        if (!isNil(data.scheduledReleaseAt) && data.scheduledReleaseAt !== "") {
            formData.append("scheduledReleaseAt", new Date(data.scheduledReleaseAt).toISOString());
        }
        if (!isNil(this.coverImageUploadViewStore.uploadedFileId)) {
            formData.append("coverImageId", this.coverImageUploadViewStore.uploadedFileId);
        }

        try {
            await this.routeStore.uploadAlbumTrack(this.albumId, formData);
        } catch (error) {
            if (error.statusCode === httpStatusCodes.Conflict)
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.NAKI_NIGUN.SONGS.UPLOAD.ERROR.ALREADY_EXIST"),
                    error
                );
            else {
                this.rootStore.notificationStore.error(
                    localizationService.t("ADMINISTRATION.NAKI_NIGUN.SONGS.UPLOAD.ERROR.GENERIC"),
                    error
                );
            }
        }
    }

    attachAudioFile = (event) => {
        this.fileUploadStore.raiseBrowse(event);
    };

    removeAudioFile = () => {
        this.fileUploadStore.clear();
    };

    _createNavigateBackAction(album) {
        if (album) {
            return {
                title: album.title,
                routeName: "master.administration.naki-nigun.artist.album.tracks",
                routeParams: { id: album.id },
            };
        }
    }
}

export default NakiNigunAlbumTrackEditViewStore;
